/**
 * Team Design — Quick Customization CTA click handler.
 *
 * Wired up by the two inline links in the Quick Customization bottom
 * feature card:
 *   • data-qc-action="ds" → "Design Service" (Design Services handoff)
 *   • data-qc-action="dt" → "3D Designer"     (opens the 3D design tool)
 *
 * Both branches share the entire pipeline (parse first-collection SKU,
 * fetch bundle + design-options, POST /projects/create-v2, PUT
 * /projects/<id>/roster). They differ in only two things — the
 * `creationOrigin` sent on create-v2 and the URL we redirect to on
 * success:
 *   • DS → creationOrigin='DS', redirect to `<custom_url>/projects/<id>`
 *          (customer portal — the DS team picks the project up there).
 *   • DT → creationOrigin='DT', redirect to `<designToolUrl>/<id>` where
 *          `designToolUrl` is `<custom_url minus /account>/design/projects`
 *          — same host, different sub-path — matching the calculator's
 *          `Edit in SquadStudio™` handler in
 *          libs/frontend/calculator/src/shopify-checkout-util.ts (the
 *          `createProject('DT')` branch).
 *
 * Source SKU: the FIRST product card on the current collection page
 * (`#product-grid .grid__item[data-sku]`) — same selector the
 * `main-collection-product-grid.liquid` debug script uses. The SKU is
 * the only product-specific input we need; everything else (apparel
 * flag, shop code, currency, turnaround) is the same for every
 * apparel collection.
 *
 * OFF-COLLECTION triggers: a CTA can also carry its own product
 * context, which is what lets non-collection templates reuse this exact
 * flow instead of forking it (today: the "What's Your Game?" sport
 * cards in sections/collection-cards.liquid, which render on the home
 * page where no `#product-grid` exists):
 *   • `data-qc-sku="<compound sku>"`   — full storefront SKU, parsed the
 *     same way as a grid SKU so PS/CT still resolve. Liquid renders it
 *     from the card's own collection (`collection.products.first`).
 *   • `data-qc-bundle-code="<CODE>"`   — bare bundle code, used only when
 *     no SKU is available (empty collection / product without a SKU).
 * When neither is present we fall back to the collection-grid scrape,
 * so existing collection-page CTAs are unaffected.
 *
 * Redirect target is selectable with `data-qc-redirect`:
 *   • (absent)          → legacy behavior (portal for DS, design tool for DT)
 *   • 'design-request'  → `<portal>/projects/<id>/design-request?sport=<bundleCode>`
 *     — the Design Services intake page, which needs the bundle code in
 *     the query string to preselect the sport.
 *   • 'builder'         → `<portal-root>/builder/<bundleCode>`
 *     — the public package builder. This one does NOT create a project:
 *     the route takes neither a project nor a sign-in, so the whole
 *     create-v2 + roster sequence below is skipped and the click is a
 *     plain navigation. See customer-portal `(routes)/builder/[sport]`.
 *
 * Two-step API sequence (mirrors the calculator):
 *   1. GET  `<scriptsHost>/api/squad/rest/bundle_by_code/<code>`
 *      — loads the bundle config (apparel = uniform = jersey + shorts +
 *      etc.) so we can build a real `items` array. This endpoint is on
 *      the `scripts.*` host that `window.serviceApiUrl` points at.
 *      We also pull the bundle's pattern-set / color-theme IDs from
 *      `window.CollectionDesignOptions.fetch` (cached at page-load by
 *      team-design.swatches.js) — same source the per-card swatches
 *      use, so there's no duplicate network round-trip.
 *   2. POST `<apiHost>/api/squad/projects/create-v2`
 *      — the squad CRUD API lives on the SIBLING `api.*` host
 *      (apparel-theme's own scripts/utils/config.js draws the same
 *      distinction); we string-swap `scripts.` → `api.` to reach it.
 *
 * URL sources (both set once in theme.liquid for the whole storefront):
 *   - `window.serviceApiUrl` (= scripts.squadmojo-<stage>.com)
 *      → bundle lookup (host as-is)
 *      → create-v2 + roster (string-swap `scripts.` → `api.`)
 *   - `window.custom_url`    (= app.squadmojo-<stage>.com/account)
 *      → portal redirect (+ `/projects/<id>`)
 *
 * After the project is created, the script ALSO mirrors the
 * calculator's `afterProjectCreated` roster save (see
 * libs/frontend/calculator/src/calculator.squad.ts) — if the shopper
 * typed a team name OR uploaded a logo in the Quick Customization
 * panel, those land in the parent-domain `team_design` cookie via
 * `window.TeamDesignStorage`, and we PUT them to
 * `/projects/<id>/roster` so the DS team sees them when they open
 * the project in design-tools. The roster call is best-effort: a
 * failure is logged but does NOT block the redirect, matching the
 * calculator's behavior.
 *
 * Failure modes (each surfaces as a console error + the link snaps
 * back to its idle state so the shopper can retry):
 *   - No `#product-grid .grid__item[data-sku]` on the page (e.g. the
 *     CTA somehow renders outside a collection) — abort, no fetch.
 *   - Network/server failure — abort, no redirect.
 *   - 4xx/5xx from the API — abort, no redirect.
 */
(function () {
  'use strict';

  // ─── Config (from theme.liquid globals) ──────────────────────────────────
  function resolveHosts() {
    // Everything comes from window.APP_CONFIG (snippets/app-config.liquid).
    // The dev fallbacks only matter on local Shopify CLI previews where the
    // layout never rendered APP_CONFIG.
    var cfg        = (typeof window !== 'undefined' && window.APP_CONFIG) || null;
    var serviceApi = (cfg && cfg.roots.scripts)    || 'https://scripts.squadmojo-dev.com';
    var customUrl  = (cfg && cfg.endpoints.portal) || 'https://app.squadmojo-dev.com/account';

    // create-v2 + roster endpoints live on `api.*`, not `scripts.*`.
    // The `scripts.*` host only serves storefront REST helpers + the
    // calculator API (including `bundle_by_code`) — POSTing
    // create-v2 to it triggers a CORS preflight that fails because the
    // squad CRUD API isn't routed there. See apparel-theme/
    // scripts/utils/config.js (`projects:` vs `bundle:`/`product:`/
    // `productSku:` entries) for the same split.
    var apiHost = serviceApi.replace('scripts.', 'api.');

    // designToolUrl mirrors the calculator's per-shop `designToolUrl`
    // config (libs/frontend/calculator/src/shopify-checkout-util.ts —
    // `https://app.squadmojo-<stage>.com/design/projects`). We derive it
    // from `custom_url` by swapping `/account` for `/design/projects` so
    // both flows keep pointing at the same host without needing a second
    // globals declaration in theme.liquid.
    var designToolUrl = customUrl.replace(/\/account\b/, '/design/projects');
    // Safety net for the `custom_url` fallback (`.../account`) so
    // string-swap misses don't ship a broken redirect.
    if (designToolUrl === customUrl) {
      designToolUrl = customUrl.replace(/\/$/, '') + '/design/projects';
    }

    // Host + env-correct /api/<apiPath> (squad on prod, squad on dev/qa)
    // come from snippets/app-config.liquid. Fallbacks (dev + 'squad') only
    // matter on local Shopify CLI previews where APP_CONFIG never rendered.
    return {
      scriptsUrl:    (cfg && cfg.designApi)     || serviceApi + '/api/squad', // bundle_by_code lives here
      apiUrl:        (cfg && cfg.designApiCrud) || apiHost    + '/api/squad', // create-v2 + roster
      portalUrl:     customUrl  + '/projects',         // DS → customer portal
      builderUrl:    customUrl  + '/builder',          // public package builder
      designToolUrl: designToolUrl,                    // DT → 3D design tool
    };
  }

  // ─── Cookie reader (theme.liquid exposes one too but it's defined
  // inside an IIFE we can't reuse — keep a local copy so this file
  // is self-contained). ────────────────────────────────────────────────────
  function getCookie(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  // ─── Read the first product SKU off the collection grid ──────────────────
  function readFirstCollectionSku() {
    // `data-sku` is rendered onto `.card-wrapper` by card-product.liquid
    // (see card-product.liquid: `data-sku="{{ card_product
    // .selected_or_first_available_variant.sku }}"`). The grid items
    // themselves don't carry the SKU — so the selector targets the
    // inner card. First match in document order = the first product
    // the shopper sees.
    //
    // The OR chain also matches `[data-product-sku]` and any other
    // `[data-sku]` carrier under `#product-grid`, so this keeps working
    // if a future card layout shifts the attribute to a different node.
    var firstCard = document.querySelector(
      '#product-grid .card-wrapper[data-sku], #product-grid [data-sku], #product-grid [data-product-sku]'
    );
    if (!firstCard) return null;
    var sku = firstCard.getAttribute('data-sku') ||
              firstCard.getAttribute('data-product-sku') ||
              (firstCard.dataset && (firstCard.dataset.sku || firstCard.dataset.productSku)) || '';
    sku = String(sku).trim();
    return sku || null;
  }

  // Resolve the SKU for a given trigger. A trigger that carries its own
  // `data-qc-sku` wins (off-collection CTAs like the "What's Your Game?"
  // sport cards, where Liquid already knows the collection's first
  // product); everything else falls back to scraping the collection grid
  // on the current page.
  function readTriggerSku(link) {
    var explicit = link && link.getAttribute ? link.getAttribute('data-qc-sku') : null;
    explicit = String(explicit || '').trim();
    if (explicit) return explicit;
    return readFirstCollectionSku();
  }

  // ─── Compound-SKU parsing ────────────────────────────────────────────────
  // Apparel storefront `data-sku` is shaped like
  //   "<BUNDLE_CODE>:::PS:<patternSetCode>-CT:<colorThemeCode>-<VARIANT_SKU>"
  // We delegate to `window.CollectionDesignOptions.parseSku` (defined in
  // team-design.swatches.js, which loads BEFORE this file via defer)
  // so the storefront has ONE parser instead of two. The local fallback
  // covers the defensive case where the swatches script never loaded
  // (collection page without the QC panel, partial JS failure, etc).
  function parseSkuParts(rawSku) {
    var cdo = window.CollectionDesignOptions;
    if (cdo && typeof cdo.parseSku === 'function') {
      var p = cdo.parseSku(rawSku);
      return {
        bundleCode: (p && p.code) || null,
        patternSetCode: (p && p.ps) || null,
        colorThemeCode: (p && p.ct) || null,
      };
    }
    // Defensive fallback (mirrors swatches.parseSku's logic).
    var parts = String(rawSku || '').split(':::');
    var bundleCode = (parts[0] || '').trim();
    var variantPart = parts[1] || '';
    var psMatch = variantPart.match(/(?:^|-)PS:([^-]+)/);
    var ctMatch = variantPart.match(/(?:^|-)CT:([^-]+)/);
    return {
      bundleCode: bundleCode || null,
      patternSetCode: psMatch ? psMatch[1] : null,
      colorThemeCode: ctMatch ? ctMatch[1] : null,
    };
  }

  // Resolve a code → id off a `[{ id, code }]` list. When the code
  // doesn't match (or isn't provided) we fall back to the FIRST entry —
  // same fallback the calculator's `pickColorThemeFromList` helper uses
  // (calculator.ts) so the project always boots with a usable pattern /
  // color instead of a blank slot.
  function findIdByCode(list, code) {
    if (!list || !list.length) return null;
    if (code != null && code !== '') {
      for (var i = 0; i < list.length; i++) {
        var item = list[i];
        if (item && String(item.code) === String(code)) {
          return item.id || null;
        }
      }
    }
    return (list[0] && list[0].id) || null;
  }

  // Read the QC banner font selection (set by team-design.banner-swatches.js
  // when the shopper taps a font tile) — gets forwarded as `meta.fontName`
  // so design-tools opens the project with the same family the shopper
  // saw in the storefront preview. Safe-accessed because this script
  // can load even when the QC panel never finished initialising.
  function readBannerFontName() {
    try {
      var state = window.CollectionTeamText && window.CollectionTeamText.state;
      var f = state && state.bannerTextFont;
      var fam = f && f.family;
      return fam ? String(fam) : null;
    } catch (e) {
      return null;
    }
  }

  // Read the QC banner color theme — set when the shopper clicks a swatch
  // and then mutated when they edit a C1/C2/C3/C4 chip. The picker clears
  // `theme.code` on every chip edit (banner-swatches.js → `previewColor`),
  // so we can distinguish:
  //   - `theme.code` non-empty  → registered preset → resolve colorThemeId
  //                               via design-options
  //   - `theme.code` empty + colors → custom theme → send
  //                                  `meta.customColorTheme` + colorThemeId: null
  // Same branching the calculator uses (shopify-checkout-util.ts:
  // `customColorTheme ? null : selectedColorTheme.id`).
  function readBannerTheme() {
    try {
      var state = window.CollectionTeamText && window.CollectionTeamText.state;
      return (state && state.bannerTheme) || null;
    } catch (e) {
      return null;
    }
  }

  // Tight 3/6-digit hex normaliser (`#abc` → `#AABBCC`). Mirrors
  // `normalizeHexColor` in libs/frontend/calculator/src/utils/
  // custom-color-theme.ts so the storefront-generated meta is byte-for-
  // byte identical to what the calculator would have produced for the
  // same RGB values. Falls back to the supplied default when the input
  // can't be parsed as a hex triple.
  function normalizeHex(value, fallback) {
    var raw = String(value == null ? '' : value).trim();
    var hex = raw.charAt(0) === '#' ? raw : '#' + raw;
    if (/^#[0-9a-f]{3}$/i.test(hex)) {
      return ('#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]).toUpperCase();
    }
    if (/^#[0-9a-f]{6}$/i.test(hex)) {
      return hex.toUpperCase();
    }
    return fallback;
  }

  // Build the `meta.customColorTheme` value — same shape +
  // role-key mapping that `createCustomColorThemeMeta` in
  // libs/frontend/calculator/src/utils/custom-color-theme.ts produces,
  // so design-tools sees identical data regardless of which entry
  // point (calculator or storefront CTA) created the project.
  function buildCustomColorThemeMeta(theme) {
    if (!theme) return null;
    // Same DEFAULT_CUSTOM_COLOR_THEME values the calculator falls back
    // to (utils/custom-color-theme.ts).
    var pri = normalizeHex(theme.primaryColor,   '#1E88E5');
    var sec = normalizeHex(theme.secondaryColor, '#43A047');
    var ter = normalizeHex(theme.tertiaryColor,  '#FB8C00');
    var acc = normalizeHex(theme.accentColor,    '#E53935');
    return {
      primary:   { id: 'primary',   color: { solid: pri, type: 'solid' }, transparency: 1 },
      secondary: { id: 'secondary', color: { solid: sec, type: 'solid' }, transparency: 1 },
      tertiary:  { id: 'tertiary',  color: { solid: ter, type: 'solid' }, transparency: 1 },
      accent:    { id: 'accent',    color: { solid: acc, type: 'solid' }, transparency: 1 },
    };
  }

  // ─── Bundle fetch ────────────────────────────────────────────────────────
  // GET `<scripts>/rest/bundle_by_code/<code>` — mirrors the calculator's
  // bundle bootstrap (libs/frontend/calculator/src/calculator.ts: the
  // `fetchWithRetry(..API_ENDPOINTS.BUNDLE..)` chain that lands on the same
  // endpoint). The response carries `bundleProducts[i] = { id, qty, sku,
  // isOptional, ... }` which we map into the `items` array below.
  function fetchBundle(bundleCode, scriptsUrl) {
    var url = scriptsUrl + '/rest/bundle_by_code/' + encodeURIComponent(bundleCode);
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Shop-Code': 'ap',
      },
    }).then(function (res) {
      if (res.status !== 200) {
        throw new Error('bundle_by_code returned ' + res.status);
      }
      return res.json();
    });
  }

  // Reuses the bundle-design-options response already fetched + cached
  // by team-design.swatches.js (`window.CollectionDesignOptions.fetch`).
  // That script runs at DOMContentLoaded and pre-warms the cache for
  // every product code on the collection page, so by click time this
  // is essentially synchronous — no extra round-trip. Falls back to a
  // direct fetch on the rare path where the swatches script never
  // loaded (e.g. defer chain interrupted). Non-fatal either way: a
  // null return means we just skip patternSetId/colorThemeId on the
  // create-v2 payload, and design-tools uses its own defaults.
  function fetchBundleDesignOptions(bundleCode, scriptsUrl) {
    var cdo = window.CollectionDesignOptions;
    if (cdo && typeof cdo.fetch === 'function') {
      return Promise.resolve(cdo.fetch(bundleCode)).catch(function () { return null; });
    }
    var url = scriptsUrl + '/rest/bundle-design-options/' + encodeURIComponent(bundleCode);
    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Shop-Code': 'ap',
      },
    })
      .then(function (res) {
        if (res.status !== 200) {
          console.warn('[request-custom-design] bundle-design-options returned ' + res.status);
          return null;
        }
        return res.json();
      })
      .catch(function (err) {
        console.warn('[request-custom-design] bundle-design-options errored', err);
        return null;
      });
  }

  // ─── Payload + request ───────────────────────────────────────────────────
  // `action` is either 'ds' (Design Service) or 'dt' (3D Designer) — only
  // affects the `creationOrigin` field. Everything else in the payload is
  // action-agnostic and comes from the collection + banner state.
  function buildPayload(parsed, bundle, designOptions, action) {
    var visitorId = getCookie('visitor_id') || undefined;

    // Mirrors the calculator's item-shape: `{ key: bundleProduct.id,
    // qty: bundleProduct.qty, sku: bundleProduct.sku }` (see
    // libs/frontend/calculator/src/shopify-checkout-util.ts where
    // `calcResult.items` is mapped into the create-v2 body). We include
    // every bundle product with a SKU — apparel bundles like the soccer
    // uniform have two pieces (jersey + shorts) and the DS team needs
    // both. Filtering on `isOptional` here was the source of the earlier
    // missing-shorts bug; the calculator instead respects
    // `selected`/user-toggles, but for the DS handoff sending the whole
    // default bundle is the closer match to "what the shopper saw".
    var bundleProducts = (bundle && bundle.bundleProducts) || [];
    var items = [];
    for (var i = 0; i < bundleProducts.length; i++) {
      var bp = bundleProducts[i];
      if (!bp || !bp.sku) continue;
      items.push({
        key: bp.id,
        qty: bp.qty || 1,
        sku: bp.sku,
      });
    }

    // Resolve patternSetId from the SKU's `PS:<code>` (no QC-panel
    // override for pattern set today — it's per-product, not banner-wide).
    var patternSetId = findIdByCode(
      (designOptions && designOptions.bundlePatternSets) || [],
      parsed.patternSetCode,
    );

    // Resolve colorThemeId — three branches matching the calculator
    // (libs/frontend/calculator/src/shopify-checkout-util.ts):
    //   1) Banner preset picked      → look up by `theme.code`
    //   2) Banner custom (C1-C4 edit) → `colorThemeId: null` + meta.customColorTheme
    //   3) No banner interaction     → fall back to the SKU's `CT:<code>`
    var bannerTheme = readBannerTheme();
    var bannerHasColors = !!(bannerTheme && (
      bannerTheme.primaryColor || bannerTheme.secondaryColor ||
      bannerTheme.tertiaryColor || bannerTheme.accentColor
    ));
    var bannerIsCustom = !!bannerTheme && !bannerTheme.code && bannerHasColors;
    var bannerIsPreset = !!bannerTheme && !!bannerTheme.code;

    var colorThemeId = null;
    var customColorTheme = null;
    if (bannerIsCustom) {
      // Calculator sends `colorThemeId: null` + `meta.customColorTheme`
      // when the shopper has edited any chip — same here.
      customColorTheme = buildCustomColorThemeMeta(bannerTheme);
    } else if (bannerIsPreset) {
      colorThemeId = findIdByCode(
        (designOptions && designOptions.bundleColorThemes) || [],
        bannerTheme.code,
      );
    } else {
      // No banner interaction — use the SKU's pinned CT code (with the
      // findIdByCode fallback to the first theme in the list).
      colorThemeId = findIdByCode(
        (designOptions && designOptions.bundleColorThemes) || [],
        parsed.colorThemeCode,
      );
    }

    // Build the `meta` object — only include fields that have a value.
    // Matches the calculator's `meta = { ...(customColorTheme ? {customColorTheme} : {}), ...(textFontName ? {fontName} : {}) }`.
    var meta = {};
    if (customColorTheme) meta.customColorTheme = customColorTheme;
    var fontName = readBannerFontName();
    if (fontName) meta.fontName = fontName;

    var payload = {
      bundleCode: parsed.bundleCode,
      visitorId: visitorId,
      shopCode: 'ap',
      currencyCode: 'USD', // storefront default; backend persists for price reference
      isApparel: !!(bundle && bundle.isApparel),
      sizeFit: null,
      turnaroundType: 'REG', // storefront CTA requests are always REG
      creationOrigin: action === 'dt' ? 'DT' : 'DS',
      initialPage: (typeof window !== 'undefined' && window.location && window.location.href) || undefined,
      items: items,
    };
    if (patternSetId) payload.patternSetId = patternSetId;
    // Send `colorThemeId: null` explicitly in the custom-theme case
    // (the calculator does the same — the backend uses the explicit
    // null to know the project is on a custom theme rather than a
    // preset). For the preset / fallback branches we still gate on a
    // truthy id so we don't write `null` for a missing lookup.
    if (bannerIsCustom) {
      payload.colorThemeId = null;
    } else if (colorThemeId) {
      payload.colorThemeId = colorThemeId;
    }
    if (Object.keys(meta).length > 0) payload.meta = meta;
    return payload;
  }

  function createProject(payload, apiUrl) {
    return fetch(apiUrl + '/projects/create-v2', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Shop-Code': 'ap',
      },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (res.status === 201) return res.json();
      // Non-201 from the API: surface the status so the New Relic
      // console hook (installed by theme.liquid) attributes it to
      // this widget. Return null so the caller skips the redirect.
      console.error('[request-custom-design] create-v2 failed', res.status);
      return null;
    });
  }

  // ─── Team-design cookie → roster sync ────────────────────────────────────
  // Prefer the `window.TeamDesignStorage` API (loaded by
  // team-design.storage.js, which the QC snippet loads with `defer`
  // before this file), and fall back to a direct cookie read so the
  // roster call still works if the storage module ever fails to load.
  function readTeamDesign() {
    if (window.TeamDesignStorage && typeof window.TeamDesignStorage.get === 'function') {
      var stored = window.TeamDesignStorage.get();
      return stored && typeof stored === 'object' ? stored : {};
    }
    try {
      var match = document.cookie.match(/(?:^|; )team_design=([^;]*)/);
      if (!match) return {};
      var parsed = JSON.parse(decodeURIComponent(match[1]));
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  // PUT /projects/<id>/roster — mirrors `afterProjectCreated` in
  // libs/frontend/calculator/src/calculator.squad.ts. Resolves even on
  // failure so the caller's redirect always runs.
  function saveRoster(projectId, apiUrl) {
    var td = readTeamDesign();
    var teamName = (td.teamName != null ? String(td.teamName) : '').trim();
    var logoMediaId = (td.logo && td.logo.mediaId != null ? String(td.logo.mediaId) : '').trim();

    // No team data → nothing to save. The DS team will fill it in
    // manually from the customer portal.
    if (!teamName && !logoMediaId) return Promise.resolve();

    return fetch(apiUrl + '/projects/' + encodeURIComponent(projectId) + '/roster', {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Shop-Code': 'ap',
      },
      body: JSON.stringify({
        name: teamName,
        logo: logoMediaId,
        // sizeFit isn't captured by the storefront QC panel — design-tools
        // prompts for it later. `items: []` matches the calculator's
        // empty-roster default (the DS team adds player rows in DT).
        sizeFit: null,
        items: [],
      }),
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('Roster save failed with status ' + res.status);
        }
      })
      .catch(function (err) {
        // Match the calculator: log + swallow so the redirect proceeds.
        console.error('[request-custom-design] roster save failed', err);
      });
  }

  // ─── Loading state on the link itself ────────────────────────────────────
  // Toggles `aria-busy` + `disabled` on the button and swaps the inner
  // `.qc-cta-card__label` text. The spinner element is shown purely via
  // CSS (`[aria-busy="true"] .qc-cta-card__spinner { display: inline-block }`),
  // so no JS class juggling is required.
  function setLoading(link, isLoading, action) {
    if (!link) return;
    var label = link.querySelector('.qc-cta-card__label');
    if (isLoading) {
      if (label) {
        if (!label.dataset.qcCtaIdleLabel) {
          // Cache whatever the idle label was ("Design Service" or
          // "3D Designer") so we can restore it after the request
          // regardless of which CTA the shopper tapped.
          label.dataset.qcCtaIdleLabel = label.textContent || '';
        }
        // Per-action loading label — matches the button verb ("Design
        // Service" → "Requesting design…", "3D Designer" → "Opening
        // designer…") so the swap stays tight and hints at the
        // redirect target during the 1–2 second wait.
        label.textContent = action === 'dt' ? 'Opening designer…' : 'Requesting design…';
      }
      link.setAttribute('aria-busy', 'true');
      link.disabled = true;
    } else {
      if (label && label.dataset.qcCtaIdleLabel) {
        label.textContent = label.dataset.qcCtaIdleLabel;
      }
      link.removeAttribute('aria-busy');
      link.disabled = false;
    }
  }

  // ─── Click handler (event delegation) ────────────────────────────────────
  // Delegated on `document` instead of bound directly to the link so we
  // don't care WHEN the link appears in the DOM — other team-design.*
  // scripts reparent the QC sidebar (banner-swatches.relocatePanelIntoGrid)
  // and panel-slots toggles pane visibility, both of which could otherwise
  // race a direct-bind approach. A single document-level listener side-
  // steps all of that.
  function handleDocumentClick(e) {
    var link = e.target && e.target.closest ? e.target.closest('.qc-cta-card__link') : null;
    if (!link) return;

    // The link's `href="#"` would otherwise jump the viewport to top.
    e.preventDefault();

    if (link.getAttribute('aria-busy') === 'true') return; // already in flight

    // `data-qc-action` selects the branch — 'dt' opens the 3D design tool,
    // anything else (including a missing attribute) falls back to the
    // Design Service portal so a stray CTA without the attribute keeps
    // working the old way.
    var action = (link.getAttribute('data-qc-action') || 'ds').toLowerCase();
    var isDt = action === 'dt';
    // 'design-request' sends the shopper to the DS intake page instead of
    // the plain portal project page. Any other value keeps legacy behavior.
    var redirectMode = (link.getAttribute('data-qc-redirect') || '').toLowerCase();

    // When the trigger is an `<a href>` (the sport cards degrade to a plain
    // collection link without JS), keep that href as the escape hatch for
    // the "no bundle code" dead ends below — doing nothing on click would
    // read as a broken card.
    var fallbackHref = link.tagName === 'A' ? link.getAttribute('href') : null;
    if (fallbackHref === '#' || fallbackHref === '') fallbackHref = null;

    var sku = readTriggerSku(link);
    var parsed = sku
      ? parseSkuParts(sku)
      : { bundleCode: null, patternSetCode: null, colorThemeCode: null };

    // `data-qc-bundle-code` is the last resort: a trigger whose collection
    // has no product (or no SKU on it) still knows which bundle it means.
    if (!parsed.bundleCode) {
      var declaredCode = String(link.getAttribute('data-qc-bundle-code') || '').trim();
      if (declaredCode) parsed.bundleCode = declaredCode;
    }

    if (!parsed.bundleCode) {
      console.error(
        '[request-custom-design] Could not derive bundle code (sku=' + sku + ') — skipping create-project.'
      );
      if (fallbackHref) window.location.href = fallbackHref;
      return;
    }

    var hosts = resolveHosts();

    // The package builder is a straight navigation, not a project flow.
    // `<portal-root>/builder/<bundleCode>` takes no project and no sign-in
    // (customer-portal `(routes)/builder/[sport]/page.tsx`), and it reads
    // the sport straight off the path — the same bundle code the create-v2
    // payload would have carried. Running the sequence below anyway would
    // only leave an orphan project behind for every card click, so bail out
    // before it: no spinner, no API round-trip, no wait.
    if (redirectMode === 'builder') {
      window.location.href = hosts.builderUrl + '/' + encodeURIComponent(parsed.bundleCode);
      return;
    }

    setLoading(link, true, action);

    // 1) Fetch bundle (→ bundleProducts) + design-options (→ patternSet /
    //    colorTheme IDs) in parallel. 2) Build the create-v2 payload with
    //    the action-selected `creationOrigin` (DS or DT). 3) Create the
    //    project on the api host. 4) PUT the roster (no-op when
    //    team_design cookie is empty — the shopper's team name + logo
    //    show up in the design-tool / portal on open either way).
    //    5) Redirect to either the customer portal (DS) or the 3D
    //    design-tool (DT). Any failure in 1 or 3 surfaces in the catch
    //    below — design-options failure is non-fatal
    //    (fetchBundleDesignOptions swallows it).
    Promise.all([
      fetchBundle(parsed.bundleCode, hosts.scriptsUrl),
      fetchBundleDesignOptions(parsed.bundleCode, hosts.scriptsUrl),
    ])
      .then(function (results) {
        var bundle = results[0];
        var designOptions = results[1];
        var payload = buildPayload(parsed, bundle, designOptions, action);
        return createProject(payload, hosts.apiUrl);
      })
      .then(function (project) {
        if (!project || !project.id) {
          setLoading(link, false);
          return;
        }
        // Persist the shopper's team-design state (name + logo) on the
        // project as a roster BEFORE redirecting, so whoever opens the
        // project next (the DS team in the portal or the shopper
        // themselves in the design tool) sees the inputs from the QC
        // panel immediately. `saveRoster` swallows its own errors so a
        // failed roster never blocks navigation.
        return saveRoster(project.id, hosts.apiUrl).then(function () {
          // Design Services intake: `<portal>/projects/<id>/design-request
          // ?sport=<bundleCode>`. The bundle code is the same one the
          // project was created with, so the intake form opens on the
          // right sport without a second lookup.
          if (redirectMode === 'design-request') {
            window.location.href =
              hosts.portalUrl + '/' + project.id +
              '/design-request?sport=' + encodeURIComponent(parsed.bundleCode);
            return;
          }
          var redirectBase = isDt ? hosts.designToolUrl : hosts.portalUrl;
          // DT lands the shopper in the design tool; append `?tab=options`
          // so it opens on the Options sidebar (product/size/quantity)
          // instead of the default Design panel — matches the intent of
          // the "3D Designer" CTA (start by configuring the product, not
          // by editing artwork). Design-tools reads this in
          // apps/web/design-tools/app/experience/variants/studio/
          // toolbar-sidebar-view.tsx on first mount. DS keeps its clean
          // portal URL with no extra params.
          var suffix = isDt ? '?tab=options' : '';
          window.location.href = redirectBase + '/' + project.id + suffix;
        });
      })
      .catch(function (err) {
        console.error('[request-custom-design] flow errored', err);
        setLoading(link, false);
      });
  }

  // Reset every CTA link back to its idle state. Used by the `pageshow`
  // handler below to clear the loading spinner/label after a bfcache
  // restore.
  function resetAllLinks() {
    var links = document.querySelectorAll('.qc-cta-card__link');
    for (var i = 0; i < links.length; i++) {
      setLoading(links[i], false);
    }
  }

  // Guard against the script being included twice — if the listener is
  // already attached we'd double-fire the create-project request.
  if (!window.__qcRequestCustomDesignBound) {
    window.__qcRequestCustomDesignBound = true;
    document.addEventListener('click', handleDocumentClick);

    // On success the click handler navigates via `window.location.href`
    // while the CTA is still in its loading state (aria-busy + disabled +
    // "Requesting design…"/"Opening designer…" label). When the shopper
    // hits the browser Back button, the page is restored from the
    // back-forward cache (bfcache) in that exact frozen state — the
    // spinner keeps spinning and the button stays disabled. `pageshow`
    // fires on every navigation INCLUDING bfcache restores (where
    // `event.persisted` is true), so we snap all CTAs back to idle here.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) resetAllLinks();
    });
  }
})();
